import type { ProjectRow } from '../hooks/useProjects.js';

const DB_NAME = 'r2-project-persistence';
const DB_VERSION = 1;
const LS_LAST = 'r2.persistence.lastProjectId';
const LS_RECENT = 'r2.persistence.recentProjects';

export interface PersistentProject extends ProjectRow {
  createdAt: number;
  updatedAt: number;
  lastOpenedAt: number;
  source: 'local' | 'ipc' | 'restored';
  settings?: Record<string, unknown>;
  aiIndex?: Record<string, unknown>;
  ingestResults?: Array<Record<string, unknown>>;
  annotations?: Array<Record<string, unknown>>;
  tasks?: Array<Record<string, unknown>>;
}

export interface PersistentFileRecord {
  id: string;
  projectId: string;
  name: string;
  relativePath: string;
  type: string;
  size: number;
  lastModified: number;
  importedAt: number;
  source: string;
  blob?: Blob;
}

interface LocalProjectDb extends IDBDatabase {}

function now(): number { return Date.now(); }

function readRecentIds(): string[] {
  try { return JSON.parse(localStorage.getItem(LS_RECENT) || '[]') as string[]; } catch { return []; }
}

function writeRecentIds(ids: string[]): void {
  try { localStorage.setItem(LS_RECENT, JSON.stringify(ids.slice(0, 20))); } catch { /* ignore quota */ }
}

export function getLastProjectIdSync(): string | null {
  try { return localStorage.getItem(LS_LAST); } catch { return null; }
}

function rememberProject(id: string): void {
  try { localStorage.setItem(LS_LAST, id); } catch { /* ignore */ }
  writeRecentIds([id, ...readRecentIds().filter(x => x !== id)]);
}

function openDb(): Promise<LocalProjectDb> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('projects')) db.createObjectStore('projects', { keyPath: 'id' });
      if (!db.objectStoreNames.contains('files')) {
        const files = db.createObjectStore('files', { keyPath: 'id' });
        files.createIndex('projectId', 'projectId', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('Unable to open project persistence database'));
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
  });
}

function requestResult<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

function normalizeProject(row: Partial<ProjectRow> & { id?: string }): PersistentProject {
  const stamp = now();
  const id = row.id || `local-${Math.random().toString(36).slice(2, 10)}`;
  return {
    id,
    projectNumber: row.projectNumber || id.toUpperCase(),
    name: row.name || 'Untitled Project',
    client: row.client || 'Unknown Client',
    clientAddress: row.clientAddress,
    clientTimezone: row.clientTimezone,
    startDate: row.startDate,
    endDate: row.endDate,
    phase: row.phase || 'planning',
    status: row.status || 'active',
    color: row.color,
    createdAt: stamp,
    updatedAt: stamp,
    lastOpenedAt: stamp,
    source: 'local',
    settings: {},
    aiIndex: {},
    ingestResults: [],
    annotations: [],
    tasks: [],
  };
}

export async function getPersistentProjects(): Promise<PersistentProject[]> {
  try {
    const db = await openDb();
    const rows = await requestResult<PersistentProject[]>(db.transaction('projects', 'readonly').objectStore('projects').getAll());
    return rows.sort((a, b) => (b.lastOpenedAt || b.updatedAt) - (a.lastOpenedAt || a.updatedAt));
  } catch {
    return [];
  }
}

export async function getPersistentProject(id: string): Promise<PersistentProject | null> {
  try {
    const db = await openDb();
    return await requestResult<PersistentProject | undefined>(db.transaction('projects', 'readonly').objectStore('projects').get(id)) ?? null;
  } catch { return null; }
}

export async function savePersistentProject(row: Partial<ProjectRow> & { id?: string }, patch: Partial<PersistentProject> = {}): Promise<PersistentProject> {
  const db = await openDb();
  const tx = db.transaction('projects', 'readwrite');
  const store = tx.objectStore('projects');
  const id = row.id || patch.id || `local-${Math.random().toString(36).slice(2, 10)}`;
  const existing = await requestResult<PersistentProject | undefined>(store.get(id));
  const base = existing ?? normalizeProject({ ...row, id });
  const next: PersistentProject = {
    ...base,
    ...row,
    ...patch,
    id,
    updatedAt: now(),
    lastOpenedAt: patch.lastOpenedAt ?? base.lastOpenedAt ?? now(),
    source: patch.source ?? base.source ?? 'local',
  };
  store.put(next);
  await txDone(tx);
  rememberProject(next.id);
  return next;
}

export async function touchPersistentProject(id: string): Promise<void> {
  const existing = await getPersistentProject(id);
  if (existing) await savePersistentProject(existing, { lastOpenedAt: now() });
  else rememberProject(id);
}

export async function getLastPersistentProject(): Promise<PersistentProject | null> {
  const id = getLastProjectIdSync();
  if (id) {
    const p = await getPersistentProject(id);
    if (p) return p;
  }
  return (await getPersistentProjects())[0] ?? null;
}

export async function saveProjectFiles(projectId: string, files: File[], source = 'folder-ingest'): Promise<PersistentFileRecord[]> {
  const db = await openDb();
  const tx = db.transaction(['files', 'projects'], 'readwrite');
  const fileStore = tx.objectStore('files');
  const saved: PersistentFileRecord[] = files.map((file, index) => {
    const relativePath = file.webkitRelativePath || file.name;
    return {
      id: `${projectId}:${relativePath}:${file.size}:${file.lastModified}:${index}`,
      projectId,
      name: file.name,
      relativePath,
      type: file.type || 'application/octet-stream',
      size: file.size,
      lastModified: file.lastModified,
      importedAt: now(),
      source,
      blob: file,
    };
  });
  saved.forEach(record => fileStore.put(record));
  const project = await requestResult<PersistentProject | undefined>(tx.objectStore('projects').get(projectId));
  if (project) tx.objectStore('projects').put({ ...project, updatedAt: now(), lastOpenedAt: now() });
  await txDone(tx);
  rememberProject(projectId);
  return saved;
}

export async function listProjectFiles(projectId: string): Promise<PersistentFileRecord[]> {
  try {
    const db = await openDb();
    const idx = db.transaction('files', 'readonly').objectStore('files').index('projectId');
    return await requestResult<PersistentFileRecord[]>(idx.getAll(projectId));
  } catch { return []; }
}

export async function appendProjectIngestResult(projectId: string, result: Record<string, unknown>): Promise<void> {
  const project = await getPersistentProject(projectId) ?? await savePersistentProject({ id: projectId, projectNumber: projectId, name: projectId, client: 'Local' });
  const ingestResults = [...(project.ingestResults ?? []), { ...result, savedAt: now() }].slice(-100);
  await savePersistentProject(project, { ingestResults, aiIndex: { ...(project.aiIndex ?? {}), lastIngest: result } });
}

export async function saveProjectAnnotations(projectId: string, annotations: Array<Record<string, unknown>>): Promise<void> {
  const project = await getPersistentProject(projectId) ?? await savePersistentProject({ id: projectId, projectNumber: projectId, name: projectId, client: 'Local' });
  await savePersistentProject(project, { annotations });
}

export async function saveProjectSettings(projectId: string, settings: Record<string, unknown>): Promise<void> {
  const project = await getPersistentProject(projectId) ?? await savePersistentProject({ id: projectId, projectNumber: projectId, name: projectId, client: 'Local' });
  await savePersistentProject(project, { settings: { ...(project.settings ?? {}), ...settings } });
}